/**
 * Boutons de remise anti-gaspi d'UN lot : taux rapides, taux libre, ou badge +
 * « Retirer » quand la remise est posée. Partagés par la page Promos DLC courte
 * et la fenêtre « Anti-gaspi par lot » des fiches produit (tableau de bord et
 * caisse) : un seul geste, où qu'on soit.
 */
import { useState } from 'react';
import { api } from '../../services/api';
import { formatPrice } from '../../utils/pos.utils';
import { usePOSStore } from '../../hooks/usePOSStore';

const TAUX_RAPIDES = [20, 30, 50];

export function prixRemiseLot(prixCents: number, taux: number): number {
  return Math.round((prixCents * (100 - taux)) / 100);
}

export default function ControlesRemiseLot({
  lotId,
  promoPercent,
  prixCents,
  unite,
  onChange,
}: {
  lotId: string;
  promoPercent: number | null | undefined;
  /** Prix normal du produit (centimes), pour afficher le prix remisé. */
  prixCents: number | null | undefined;
  unite: string;
  /** Appelé après une remise posée ou retirée, avec un message pour l'opérateur. */
  onChange: (message: string | null, erreur?: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [libre, setLibre] = useState('');

  const remiser = async (taux: number) => {
    setBusy(true);
    try {
      const vendorId = usePOSStore.getState().vendorId;
      await api.patch(`/lots/${lotId}/promo`, { percent: taux, ...(vendorId ? { vendor_id: vendorId } : {}) });
      setLibre('');
      onChange(
        `Lot remisé à −${taux} %. Les caisses le proposeront après leur prochaine synchronisation du catalogue.`,
      );
    } catch (e: any) {
      onChange(null, e?.response?.data?.message || e?.message || 'Remise impossible.');
    } finally {
      setBusy(false);
    }
  };

  const retirer = async () => {
    setBusy(true);
    try {
      await api.delete(`/lots/${lotId}/promo`);
      onChange('Remise retirée.');
    } catch (e: any) {
      onChange(null, e?.response?.data?.message || e?.message || 'Retrait impossible.');
    } finally {
      setBusy(false);
    }
  };

  if (promoPercent) {
    return (
      <div className="flex items-center gap-2 shrink-0">
        <span className="px-2.5 py-1.5 rounded bg-red-50 border border-red-300 text-red-800 text-sm font-semibold whitespace-nowrap">
          −{promoPercent} %
          {prixCents ? ` → ${formatPrice(prixRemiseLot(prixCents, promoPercent))}${unite}` : ''}
        </span>
        <button
          type="button"
          onClick={() => void retirer()}
          disabled={busy}
          className="min-h-[48px] px-3 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          Retirer
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 shrink-0">
      {TAUX_RAPIDES.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => void remiser(t)}
          disabled={busy}
          className="min-h-[48px] px-3 rounded bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40"
        >
          −{t} %
        </button>
      ))}
      <input
        type="text"
        inputMode="numeric"
        placeholder="%"
        value={libre}
        onChange={(e) => setLibre(e.target.value.replace(/[^0-9]/g, ''))}
        className="w-14 min-h-[48px] px-2 border border-gray-300 rounded text-sm text-center"
        aria-label="Autre taux"
      />
      <button
        type="button"
        onClick={() => { const t = Number(libre); if (t) void remiser(t); }}
        disabled={busy || !libre}
        className="min-h-[48px] px-3 rounded border border-red-400 text-red-700 text-sm font-semibold hover:bg-red-50 disabled:opacity-40"
      >
        Remiser
      </button>
    </div>
  );
}
